<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { UserNotification } from '@/types';
import NotificationItem from './NotificationItem.vue';

defineProps<{
  notifications: UserNotification[];
  loading: boolean;
}>();

const emit = defineEmits<{
  (e: 'select', notification: UserNotification): void;
  (e: 'mark-all-read'): void;
  (e: 'view-all'): void;
  (e: 'clear-all'): void;
  (e: 'refresh'): void;
}>();

const { t } = useI18n();
</script>

<template>
  <div class="absolute right-0 mt-3 w-96 max-w-[calc(100vw-1.5rem)] rounded-xl border border-noir-700 bg-noir-900 shadow-2xl z-50">
    <div class="flex items-center justify-between px-4 py-3 border-b border-noir-700">
      <h3 class="text-sm font-semibold text-cream-100">{{ t('notifications.dropdown.title') }}</h3>
      <button type="button" class="text-xs text-noir-300 hover:text-gold-400" @click="emit('refresh')">
        {{ t('notifications.actions.refresh') }}
      </button>
    </div>

    <div class="max-h-96 overflow-y-auto p-3 space-y-2">
      <p v-if="loading" class="text-xs text-noir-300 px-1">{{ t('notifications.loading') }}</p>
      <p v-else-if="notifications.length === 0" class="text-xs text-noir-300 px-1">{{ t('notifications.empty') }}</p>

      <NotificationItem
        v-for="notification in notifications"
        :key="notification.id"
        :notification="notification"
        @select="(item) => emit('select', item)"
      />
    </div>

    <div class="grid grid-cols-2 gap-2 p-3 border-t border-noir-700">
      <button type="button" class="text-xs rounded border border-noir-600 px-3 py-2 text-noir-200 hover:text-gold-400" @click="emit('mark-all-read')">
        {{ t('notifications.actions.markAllRead') }}
      </button>
      <button type="button" class="text-xs rounded border border-noir-600 px-3 py-2 text-noir-200 hover:text-burgundy-300" @click="emit('clear-all')">
        {{ t('notifications.actions.clearAll') }}
      </button>
      <button type="button" class="col-span-2 text-xs rounded border border-gold-400/60 px-3 py-2 text-gold-300 hover:bg-gold-400/10" @click="emit('view-all')">
        {{ t('notifications.dropdown.viewAll') }}
      </button>
    </div>
  </div>
</template>
